import React, { useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Link,
  TextField,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
  Tooltip,
  Button,
  IconButton,
  Typography,
  Checkbox,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { ExpandLess, ExpandMore, PowerSettingsNew, Check, Delete } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import isCidr from 'is-cidr';
import { isIP } from 'is-ip';
import { AUTH_LEVELS } from '../../redux/constants/roles';
import {
  createDeploymentCollectorAction,
  upsertDeploymentAction,
  updateDeploymentCollectorAction,
} from '../../redux/actions/deployments';
import { nullerize } from '../../utils/strings';
import { dateTime } from '../../utils/parsing';
import { defaultTableOptions } from '../util/data-table';

const ovaUrl = 'https://onboard-releases.s3.amazonaws.com/collector/onboard-24-20260915.ova';
const standardMode = 'onboard-24';

const DeploymentConfiguration = ({
  deployment,
  collectors,
  canDisable,
  canCreate,
  canReenable,
  updateDeployment,
  updateCollector,
  createCollector,
}) => {
  const confirm = useConfirm();
  const [isActive, setIsActive] = useState(deployment.active, [deployment]);
  const [isCreate, setIsCreate] = useState(false);
  const [createMode, setCreateMode] = useState(standardMode);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsActive(deployment.active);
  }, [deployment.active]);

  const data = useMemo(
    () => collectors.toSorted((c1, c2) => c2.created - c1.created),
    [collectors]
  );
  const columns = useMemo(() => {
    const activeCell = ({ renderedCellValue }) =>
      renderedCellValue ? <Check variant="contained" color="success" /> : null;
    return [
      {
        accessorKey: 'active',
        header: 'Active',
        Cell: activeCell,
      },
      {
        header: 'Created At',
        accessorFn: ({ created }) => dateTime(created),
      },
      {
        header: 'IPs',
        accessorFn: ({ device_ips }) =>
          // eslint-disable-next-line camelcase
          device_ips?.toSorted()?.join(', '),
      },
      {
        accessorKey: 'disabled',
        header: 'Disabled At',
        accessorFn: ({ disabled }) => dateTime(disabled),
      },
    ];
  }, []);
  const collectorTable = useMaterialReactTable({
    ...defaultTableOptions,
    columns,
    data,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    enableBottomToolbar: canCreate,
    enableRowActions: canDisable,
    getRowId: (row) => row.id,
    positionActionsColumn: 'last',
    muiEditTextFieldProps: {
      variant: 'outlined',
    },
    renderRowActions: ({ row }) => {
      if (row.original.active && canDisable) {
        return (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <IconButton
              onClick={() => {
                confirm({
                  title: `Disable collector created at ${new Date(row.original.created).toDateString()}`,
                  description: `This action will disable the collector shortly. Disabled collectors are deleted after 30 days. Do you wish to proceed?`,
                  confirmationText: 'Disable Collector',
                }).then(({ confirmed }) => {
                  if (confirmed) {
                    updateCollector({ ...row.original, active: false });
                  }
                });
              }}
            >
              <Delete color="error" />
            </IconButton>
          </Box>
        );
      }
      if (canReenable && !row.original.active) {
        return (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <IconButton
              onClick={() => {
                updateCollector({ ...row.original, active: true });
              }}
            >
              <PowerSettingsNew />
            </IconButton>
          </Box>
        );
      }
      return null;
    },
    renderBottomToolbarCustomActions: () => (
      <Button variant="contained" onClick={() => setIsCreate(true)}>
        Create {collectors?.length === 0 ? '' : 'Replacement '}Collector
      </Button>
    ),
  });
  return (
    <>
      <Dialog open={isCreate}>
        <DialogTitle variant="h4">Add Replacement Collector</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <DialogContentText>
            Only one collector for a deployment should be active at a time. Please make sure
            previous collectors are offline before starting up the new collector
          </DialogContentText>
          <form
            autoComplete="off"
            id="collector-form"
            onSubmit={(event) => {
              event.preventDefault();
              const nameservers =
                event.currentTarget.elements.nameservers?.value
                  ?.split(',')
                  .map(nullerize)
                  .filter(Boolean) ?? [];
              createCollector(deployment.site, {
                type: createMode,
                audit_key: nullerize(event.currentTarget.elements.audit_key?.value),
                static_cidr: nullerize(event.currentTarget.elements.static_cidr?.value),
                static_gateway: nullerize(event.currentTarget.elements.static_gateway?.value),
                nameservers: nameservers.length === 0 ? null : nameservers,
                wireguard: {
                  ip: nullerize(event.currentTarget.elements.wg_ip?.value),
                  private_key: nullerize(event.currentTarget.elements.wg_key?.value),
                },
              }).then(({ iso, env }) => {
                let mimeType;
                let name;
                let binaryString;
                if (iso) {
                  mimeType = 'application/gzip';
                  name = `${createMode}-${deployment.site}.iso.gz`;
                  // Decode base64 to bytes
                  binaryString = atob(iso);
                } else {
                  mimeType = 'application/json';
                  name = `${createMode}-${deployment.site}.json`;
                  // Decode base64 to bytes
                  binaryString = atob(env);
                }
                const bytes = new Uint8Array(binaryString?.length);
                for (let i = 0; i < binaryString?.length; i += 1) {
                  bytes[i] = binaryString.charCodeAt(i);
                }

                // Create blob from decompressed data and trigger download
                const decompressedBlob = new Blob([bytes], { type: mimeType });
                const url = window.URL.createObjectURL(decompressedBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setIsCreate(false);
                setIsCreated(true);
              });
            }}
          >
            <ToggleButtonGroup
              exclusive
              value={createMode}
              onChange={(_, mode) => setCreateMode(mode)}
              variant="contained"
            >
              <ToggleButton value={standardMode}>VM</ToggleButton>
              <ToggleButton value="neeve">Neeve</ToggleButton>
            </ToggleButtonGroup>
            {createMode === standardMode ? (
              <>
                <Tooltip title="Static CIDR block and gateway for the device to use, only required if you need the appliance to acquire a specific IP address. If not defined, device will use DHCP on host network to acquire an IP.">
                  <DialogContentText>(Optional) Device Static Networking:</DialogContentText>
                  <DialogContentText>
                    Static IP Cidr:{' '}
                    <TextField
                      id="static_cidr"
                      defaultValue={null}
                      error={!!validationErrors?.static_cidr}
                      helperText={validationErrors?.static_cidr}
                      onBlur={({ target }) =>
                        setValidationErrors({
                          ...validationErrors,
                          static_cidr:
                            nullerize(target.value) === null || isCidr(target.value)
                              ? null
                              : 'Must be a valid CIDR or empty',
                        })
                      }
                    />
                    <br />
                    Gateway IP:{' '}
                    <TextField
                      id="static_gateway"
                      defaultValue={null}
                      error={!!validationErrors?.static_gateway}
                      helperText={validationErrors?.static_gateway}
                      onBlur={({ target }) =>
                        setValidationErrors({
                          ...validationErrors,
                          static_gateway:
                            nullerize(target.value) === null || isIP(target.value)
                              ? null
                              : 'Must be a valid IP or empty',
                        })
                      }
                    />
                  </DialogContentText>
                </Tooltip>
                <Tooltip title="Custom DNS nameservers to use, only required if you restrict access to public DNS services. Should be defined if a static ip is in use.">
                  <DialogContentText>(Optional) DNS Nameservers:</DialogContentText>
                  <DialogContentText>
                    Nameservers:{' '}
                    <TextField
                      id="nameservers"
                      defaultValue={null}
                      placeholder="8.8.8.8,8.8.4.4"
                      error={!!validationErrors?.nameservers}
                      helperText={validationErrors?.nameservers}
                      onBlur={({ target }) =>
                        setValidationErrors({
                          ...validationErrors,
                          nameservers:
                            nullerize(target.value) === null || target.value.split(',').every(isIP)
                              ? null
                              : 'Must be a comma-seperated list of valid IP addresses or empty',
                        })
                      }
                    />
                  </DialogContentText>
                </Tooltip>
                <Tooltip title="SSH Public key for auditing software. If you need to run vulnerability scans against the Onboard Appliance, specify the public ssh key of your auditing software to allow access">
                  <DialogContentText>(Optional) Audit SSH Key:</DialogContentText>
                  <TextField
                    id="audit_key"
                    defaultValue={null}
                    error={!!validationErrors?.audit_key}
                    helperText={validationErrors?.audit_key}
                    onBlur={({ target }) =>
                      setValidationErrors({
                        ...validationErrors,
                        audit_key:
                          nullerize(target.value) === null ||
                          /^(ssh-rsa|ssh-ed25519|ecdsa-sha2-nistp256|ssh-dss) ([A-Za-z0-9+/]+[=]*)?(\s+([\w.-]+@[\w.-]+\.[\w]+|[\w\s]+))?$/.test(
                            target.value
                          )
                            ? null
                            : 'Must be a valid ssh public key or empty',
                      })
                    }
                  />
                </Tooltip>
                {showAdvanced ? (
                  <Tooltip title="Special keys to enable onboard remote access to the Onboard Appliance. Onboard support will provide these to you under extraordinary conditions.">
                    <DialogContentText>(Optional) Onboard WG IP:</DialogContentText>
                    <TextField
                      id="wg_ip"
                      defaultValue={null}
                      error={!!validationErrors?.wg_ip}
                      helperText={validationErrors?.wg_ip}
                      onBlur={({ target }) =>
                        setValidationErrors({
                          ...validationErrors,
                          wg_ip:
                            nullerize(target.value) === null || isIP(target.value)
                              ? null
                              : 'Must be a valid IPv4 Address',
                        })
                      }
                    />
                    <DialogContentText>(Optional) Onboard WG Private Key:</DialogContentText>
                    <TextField
                      id="wg_key"
                      defaultValue={null}
                      error={!!validationErrors?.wg_key}
                      helperText={validationErrors?.wg_key}
                      onBlur={({ target }) =>
                        setValidationErrors({
                          ...validationErrors,
                          wg_key:
                            nullerize(target.value) === null ||
                            /^[A-Za-z0-9+/]{43}=$/.test(target.value)
                              ? null
                              : 'Must be a valid wireguard private key',
                        })
                      }
                    />
                  </Tooltip>
                ) : null}
              </>
            ) : null}
          </form>
        </DialogContent>
        <DialogActions>
          {createMode === standardMode ? (
            <IconButton variant="contained" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          ) : null}
          <Button
            onClick={() => {
              setValidationErrors({});
              setIsCreate(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="collector-form"
            disabled={!Object.values(validationErrors).every((f) => f === null)}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isCreated} onClose={() => setIsCreated(false)}>
        <DialogTitle variant="h4">New Collector Ready</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <DialogContentText>
            {createMode === standardMode ? (
              <>
                The file downloaded should be gunzipped and attached to a fresh import of the
                Onboard-24 VM as a CD-ROM image. The Onboard-24 VM can be downloaded{' '}
                <Link color="secondary" href={ovaUrl}>
                  here
                </Link>{' '}
                if you haven&apos;t previously downloaded it.
              </>
            ) : (
              <>
                The file downloaded should be uploaded as a configuration file in the workflow for
                setting up a Neeve device.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsCreated(false);
            }}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
      <Box>
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
          Deployment Details
        </Typography>
        <Checkbox
          checked={isActive}
          disabled={!canDisable}
          onChange={(resp, newVal) =>
            newVal
              ? updateDeployment({ ...deployment, active: newVal })
              : confirm({
                  title: `Disable deployment ${deployment.site}`,
                  description: `This action will disable the deployment. You will not be able to re-enable it. Do you wish to proceed?`,
                  confirmationText: 'Disable Deployment',
                }).then(({ confirmed }) => {
                  if (confirmed) {
                    updateDeployment({ ...deployment, active: newVal });
                  }
                })
          }
        />
        Deployment Status
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
          Collectors
        </Typography>
        <MaterialReactTable table={collectorTable} />
      </Box>
    </>
  );
};

function mapStateToProps(state, { deployment }) {
  const {
    entities: {
      deployments: { configs },
    },
    user: { userAuthLevel },
  } = state;
  return {
    collectors: configs[deployment.site]?.collectors ?? [],
    canDisable: deployment.buildings.some((b) => b.canWrite),
    canCreate: deployment.buildings.some((b) => b.canWrite),
    canReenable: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    createCollector: (site, config) => dispatch(createDeploymentCollectorAction(site, config)),
    updateCollector: (collector) => dispatch(updateDeploymentCollectorAction(collector)),
    updateDeployment: (deployment) => dispatch(upsertDeploymentAction(deployment)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentConfiguration);
